/**
 * StatCard.tsx — Summary metric card for admin dashboard views
 *
 * Purpose:
 *   Renders a key metric in a card format: icon, label, value, and an
 *   optional trend indicator. Used on revenue and customer overview pages.
 *
 * Public API:
 *   - StatCard  Metric display card component
 *
 * @module packages/billing-admin/src/components/StatCard
 * @since 2026-05-31 (AT-50.01 — Scaffold Admin Console UI Foundation)
 */
import React from 'react';

interface StatCardProps {
  /** Card label/title (e.g. 'Total Revenue'). */
  label: string;
  /** Formatted value string to display prominently. */
  value: string;
  /** Optional emoji or icon prefix. */
  icon?: string;
  /** Trend direction — shows a coloured indicator. */
  trend?: 'up' | 'down' | 'neutral';
  /** Trend change label (e.g. '+12% vs last month'). */
  trendLabel?: string;
  /** Whether data is still loading. */
  loading?: boolean;
}

/**
 * StatCard — renders a KPI metric card.
 *
 * @since 2026-05-31 (AT-50.01)
 */
export default function StatCard({
  label,
  value,
  icon,
  trend,
  trendLabel,
  loading = false,
}: StatCardProps): React.JSX.Element {
  const trendColor =
    trend === 'up'
      ? 'text-green-600'
      : trend === 'down'
      ? 'text-red-600'
      : 'text-gray-500';

  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-5">
      <div className="flex items-center gap-2 text-sm text-gray-500 mb-1">
        {icon && <span>{icon}</span>}
        <span>{label}</span>
      </div>
      {loading ? (
        <div className="h-8 w-24 bg-gray-100 rounded animate-pulse" />
      ) : (
        <p className="text-2xl font-bold text-gray-900">{value}</p>
      )}
      {trendLabel && (
        <p className={`mt-1 text-xs ${trendColor}`}>{trendLabel}</p>
      )}
    </div>
  );
}
